import Vue from 'vue';
import Component from 'vue-class-component';
import Rating from 'common/components/Rating';
import SocialShare from 'components/SocialShare';
import currency from 'common/filters/currency';
import HeartIt from 'common/components/HeartIt';
import NewCarousel from 'components/NewCarousel';
import ProductShippingSelector from 'components/ProductShippingSelector';
import ReviewsBar from 'components/ReviewsBar';
import HighlightedReviewsSlider from 'components/HighlightedReviewsSlider/HighlightedReviewsSlider.vue';
import CartLimit from 'assets/graphql/queries/CartLimit.graphql';
import CartQuery from 'assets/graphql/queries/Cart.graphql';
import SiteConfigQuery from 'assets/graphql/queries/SiteConfig.graphql';
import getLastTld from 'common/services/utils/getLastTld';
import getParticipantsIcon from 'common/services/utils/getParticipantsIcon';
import PromoGrid from 'components/Modals/PromoGrid/PromoGrid.vue';
import { getRobotsByActivity } from 'common/utils/metainfo';
import HookTag from 'components/HookTag/HookTag.vue';

Vue.filter('currency', currency);

@Component({
  apollo: {
    cart: {
      query: CartQuery,
    },
    cart_limit: {
      query: CartLimit,
    },
    siteConfig: {
      query: SiteConfigQuery,
    },
  },
  props: {
    expirationDate: {
      type: String,
      required: true,
    },
    categoryColor: {
      type: String,
      required: true,
    },
    box: {
      type: Object,
      required: true,
    },
    activity: {
      type: Object,
      required: true,
    },
    isReserved: {
      type: Boolean,
      default: false,
    },
    changeToCreditAllowed: {
      type: Boolean,
      default: true,
    },
    isForSale: {
      type: Boolean,
      required: false,
      default: false,
    },
    boxName: {
      type: String,
      required: false,
      default: '',
    },
    isReservationBuy: {
      type: Boolean,
      default: false,
    },
    isRetailMode: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Rating,
    SocialShare,
    HeartIt,
    NewCarousel,
    ProductShippingSelector,
    ReviewsBar,
    PromoGrid,
    HookTag,
    HighlightedReviewsSlider,
  },
  metaInfo() {
    // Get the canonical URL without parameters
    const canonicalUrl =
      window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname;

    // Define the domain
    const domain = window.location.host;

    let newtitle = `Regalá ${this.activity.name} - Regalos Bigbox`;
    if (
      domain.includes('.cl') ||
      domain.includes('.com.mx') ||
      domain.includes('.com.co') ||
      domain.includes('.pe')
    ) {
      newtitle = `Regala ${this.activity.name} - Regalos Bigbox`;
    }

    const metatags = {
      title: newtitle,
      meta: [
        {
          name: 'description',
          content: this.activity.short_description,
          vmid: 'description',
        },
        { property: 'og:site_name', content: 'Bigbox', vmid: 'ogSiteName' },
        { property: 'og:title', content: newtitle, vmid: 'ogTitle' },
        { property: 'og:url', content: window.location.href, vmid: 'ogUrl' },
        { property: 'og:type', content: 'product', vmid: 'ogType' },
        {
          property: 'og:description',
          content: this.activity.short_description,
          vmid: 'description',
        },
        {
          property: 'og:image',
          content: this.activity.images[0].image,
          vmid: 'ogImage',
        },
        getRobotsByActivity(this.activity),
      ],
      link: [
        {
          rel: 'canonical',
          href: canonicalUrl, // Set the canonical URL without query parameters
          vmid: 'canonical',
        },
      ],
    };
    return metatags;
  },
})
export default class ActivityInformationComponent extends Vue {
  loading = false;
  showError = false;
  shipping = {
    type: 'digital',
    icon: 'Email2.svg',
    iconClass: 'Email2-icon',
    description: 'Envío digital por e-mail',
    isFree: true,
    isNew: false,
  };
  shippingFormats = {
    title: 'Tipos de envío:',
    options: [
      { text: 'Envío por e-mail', type: 'digital' },
      { text: 'Envío por Whatsapp', type: 'social' },
    ],
  };
  isMobile = null;
  maxMobileResolution = 768;
  isTravel = this.activity.comboactivity?.travelactivity !== undefined;
  hasHighlightedReviews = false;
  get isCatalog() {
    return (
      this.activity.activity_view_config.is_catalog &&
      this.activity.activity_view_config.catalog_case
    );
  }

  get isCartFull() {
    if (this.cart) {
      return this.cart.items.length >= this.cart_limit;
    }
    return false;
  }

  created() {
    window.addEventListener('resize', this.checkBreakpoint);
    this.checkBreakpoint();
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.checkBreakpoint);
  }

  checkBreakpoint() {
    this.isMobile = window.innerWidth <= this.maxMobileResolution;
  }

  get participantsIcon() {
    return getParticipantsIcon(this.activity.participants);
  }

  get country() {
    return getLastTld();
  }

  get activityUrl() {
    return `https://www.bigbox.com.${getLastTld()}${this.activity.product_url}`;
  }

  get hasMultipleImages() {
    return this.activity.images.length > 1;
  }

  get salesStatsText() {
    const { sales_stats } = this.activity;

    if (!sales_stats) return '';

    // Use the can_show fields that come from the backend to determine what to show
    const canShowValidatedLast24Hours =
      sales_stats?.can_show_units_validated_last_24_hours;
    const canShowValidatedLast7Days =
      sales_stats?.can_show_units_validated_last_7_days;

    // Prioritize the most recent period that the backend allows to show
    if (
      canShowValidatedLast24Hours &&
      sales_stats?.units_validated_last_24_hours
    ) {
      return `${sales_stats.units_validated_last_24_hours} personas lo disfrutaron en las últimas 24 hs`;
    } else if (
      canShowValidatedLast7Days &&
      sales_stats?.units_validated_last_7_days
    ) {
      return `${sales_stats.units_validated_last_7_days} personas lo disfrutaron en los últimos 7 días`;
    }

    return '';
  }

  updateShipping(shipping) {
    this.shipping = shipping;
  }

  accountGiftsRedirect() {
    this.$router.push({ name: 'accountGifts' });
  }

  handleReviewsLoaded(hasReviews) {
    this.hasHighlightedReviews = hasReviews;
  }
}
